/**
 * @author funfly
 * @mail 389193@qq.com
 * @date 2025-11-13
 * @copyright Gridy.Art
 */
import utils from '@/js/utils'
import conf from '@/js/conf/conf'
import paletteConf from '@/js/conf/palettes'
import download from 'downloadjs'
import JSZip from 'jszip'
import i18n from '../../lang'
import GRIDY from './GridySDK'
// eslint-disable-next-line
const $t = (key, value) => i18n.t(key, value)
const cdnHost = conf.hosts().cdnHost
const templatePath = cdnHost + 'template.3mf?' + conf.ver

// 3MF 颜色索引代码数组（与 d3PaletteArr 一一对应）
const d3PaletteIdx = [
  '4', '8', '0C', '1C', '2C', '3C', '4C', '5C', '6C', '7C',
  '8C', '9C', 'AC', 'BC', 'CC', 'DC', 'EC', '0FC', '1FC', '2FC',
  '3FC', '4FC', '5FC', '6FC', '7FC', '8FC', '9FC', 'AFC', 'BFC',
  'CFC', 'DFC', 'EFC'
]

// 3MF 颜色调色板数组（从 paletteConf 中获取）
const d3PaletteArr = paletteConf.d3Arr || []

// 3D 打印模型尺寸限制常量
const MODEL_CONSTRAINTS = {
  MIN_DIMENSION: 60, // 最小尺寸（毫米）
  MAX_DIMENSION: 200, // 最大尺寸（毫米）
  MIN_THICKNESS: 5 // 最小厚度（毫米，Z 轴）
}

const main = {
  /**
   * 将 Gridy 格式的 work JSON 数据转换为 3MF 格式的压缩包文件
   * @param {Object} work - Gridy 格式的 JSON 数据（通过 GRIDY.getFile() 获取）
   * @param {Function} cb - 回调函数 (error, blob)
   * @param {number} scale - 缩放比例（可选，默认 0 表示自动计算）
   */
  run: async function(work, cb, scale = 0) {
    try {
      if (!work) {
        return cb && cb($t('i.actFail'), null)
      }
      // 创建临时的 GRIDY 实例来获取场景数据
      const gridy = new GRIDY()
      gridy.setFile(work)
      // 获取场景数据sceneIdx = 0
      const sceneData = gridy.getSceneData(0, {})
      if (!sceneData || !sceneData.imageData) {
        return cb && cb(true, null)
      }
      // 将2D数据转换为3D模型
      const meshData = this.convert2DTo3D(sceneData)
      if (!meshData.vertices || meshData.vertices.length === 0) {
        return cb && cb($t('i.actFail'), null)
      }
      // 计算缩放
      const scaleData = this.calculateScale(meshData.vertices, scale)
      // 应用缩放
      const scaledVertices = meshData.vertices.map(v => ({
        x: v.x * scaleData.scaleX,
        y: v.y * scaleData.scaleY,
        z: v.z * scaleData.scaleZ
      }))
      // 为三角形分配颜色（匹配调色板）
      const coloredTriangles = this.assignColorsToTriangles(meshData.triangles, sceneData)
      // 获取使用的颜色列表（按使用的顺序）
      const usedColors = this.getUsedColorsFromTriangles(coloredTriangles)
      // 重新映射 paint_color（确保 paint_color 对应 usedColors 的索引）
      const remappedTriangles = this.remapPaintColors(coloredTriangles, usedColors)

      // 调试：检查颜色映射结果
      if (remappedTriangles.length > 0) {
        const sampleTriangles = remappedTriangles.slice(0, 10)
        const colorStats = {}
        sampleTriangles.forEach(t => {
          colorStats[t.paint_color] = (colorStats[t.paint_color] || 0) + 1
        })
      }
      // 加载模板文件
      // templatePath 为 3mf 的模板压缩包路径，可以直接调用
      const templateBlob = await fetch(templatePath).then(res => {
        if (!res.ok) {
          cb && cb($t('i.actFail'), null)
          return
        }
        return res.blob()
      })
      const templateZip = await JSZip.loadAsync(templateBlob)
      // 从 zip 包中读取并解析项目配置文件
      let jsonConf = {}
      const configFile = templateZip.file('Metadata/project_settings.config')
      if (configFile) {
        const configContent = await configFile.async('string')
        if (configContent && configContent.trim()) {
          jsonConf = JSON.parse(configContent)
        }
      }
      // 创建项目配置（更新 jsonConf）
      const projectConfig = this.createProjectConfig(jsonConf, usedColors)
      templateZip.file('Metadata/project_settings.config', JSON.stringify(projectConfig, null, 4))
      // 生成 3MF XML（带颜色，使用重新映射后的颜色）
      const xml = this.generate3mfXml(scaledVertices, remappedTriangles)
      templateZip.file('3D/Objects/object_14.model', xml)
      // 生成最终的 3MF 文件
      const finalBlob = await templateZip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      })
      // 使用 download 下载文件
      const fileName = (work && work.name ? work.name + '.' : '') + 'Gridy.Art.3mf'
      download(finalBlob, fileName, 'application/vnd.ms-package.3dmanufacturing-3dmodel+xml')
      cb && cb(null, fileName)
    } catch (error) {
      cb && cb(error, null)
    }
  },

  /**
   * 将 2D 像素数据转换为 3D 网格模型
   */
  convert2DTo3D: function(sceneData) {
    const vertices = []
    const triangles = []
    const vertexIndexMap = new Map()

    const cols = sceneData.cols || 0
    const rows = sceneData.rows || 0
    const imageData = sceneData.imageData || []
    const gridSize = sceneData.gridSize || 8 // 默认 8mm
    const minThickness = MODEL_CONSTRAINTS.MIN_THICKNESS // 最小厚度 5mm
    const zThickness = Math.max(gridSize, minThickness)

    if (cols === 0 || rows === 0) {
      return { vertices, triangles }
    }

    // 构建占用集合，用于判断邻居（只包含有颜色的像素）
    const occupied = new Set()
    const isValidColor = (color) => {
      if (!color || color === null || color === undefined) {
        return false
      }
      if (typeof color === 'string') {
        const trimmed = color.trim()
        return trimmed !== '' && trimmed !== '#'
      }
      return true
    }
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const index = y * cols + x
        const color = imageData[index]
        // 只添加有有效颜色的像素
        if (isValidColor(color)) {
          occupied.add(`${x},${y}`)
        }
      }
    }

    // 获取或创建顶点索引
    const getVertexIndex = (x, y, z) => {
      const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`
      if (vertexIndexMap.has(key)) {
        return vertexIndexMap.get(key)
      }
      const idx = vertices.length
      vertices.push({ x, y, z })
      vertexIndexMap.set(key, idx)
      return idx
    }

    // 立方体的六个面方向定义
    const directions = [
      // -X 面（左面）
      { n: [-1, 0], corners: (x, y) => [
        [x, y, -0.5], [x, y + 1, -0.5], [x, y, 0.5], [x, y + 1, 0.5]
      ] },
      // +X 面（右面）
      { n: [1, 0], corners: (x, y) => [
        [x + 1, y, -0.5], [x + 1, y, 0.5], [x + 1, y + 1, -0.5], [x + 1, y + 1, 0.5]
      ] },
      // -Y 面（下面）
      { n: [0, -1], corners: (x, y) => [
        [x, y, -0.5], [x, y, 0.5], [x + 1, y, -0.5], [x + 1, y, 0.5]
      ] },
      // +Y 面（上面）
      { n: [0, 1], corners: (x, y) => [
        [x, y + 1, -0.5], [x + 1, y + 1, -0.5], [x, y + 1, 0.5], [x + 1, y + 1, 0.5]
      ] },
      // -Z 面（后面）
      { n: [0, 0], corners: (x, y) => [
        [x, y, -0.5], [x + 1, y, -0.5], [x, y + 1, -0.5], [x + 1, y + 1, -0.5]
      ] },
      // +Z 面（前面）
      { n: [0, 0], corners: (x, y) => [
        [x, y, 0.5], [x, y + 1, 0.5], [x + 1, y, 0.5], [x + 1, y + 1, 0.5]
      ] }
    ]
    // 为每个像素创建外露面
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const index = y * cols + x
        let hexColor = imageData[index]

        // 如果规范化后仍然是空字符串，跳过
        if (!hexColor) {
          continue
        }

        if (!hexColor.startsWith('#')) {
          hexColor = '#' + hexColor
        }
        hexColor = hexColor.toUpperCase()

        // 如果规范化后只有 '#' 或无效的颜色格式，跳过
        // 有效的十六进制颜色应该是 #RRGGBB 格式（7个字符）
        if (hexColor === '#' || hexColor.length < 4) {
          continue
        }
        // 计算体素位置（以中心为原点，单位：gridSize）
        // const voxelX = (x - cols / 2) * gridSize
        // const voxelY = (rows / 2 - y) * gridSize // 翻转Y轴
        // 检查六个方向是否有邻居
        for (let d = 0; d < directions.length; d++) {
          const dir = directions[d]
          let hasNeighbor = false
          if (d < 4) {
            // 前4个方向检查2D邻居
            const nx = x + dir.n[0]
            const ny = y + dir.n[1]
            hasNeighbor = occupied.has(`${nx},${ny}`)
          } else {
            // Z方向的面总是外露（因为2D数据只有一层）
            hasNeighbor = false
          }
          if (hasNeighbor) continue // 有邻居则内面，跳过
          // 生成该面的两个三角形
          const cs = dir.corners(x, y)
          const corners = cs.map(c => [
            (c[0] - cols / 2) * gridSize,
            (rows / 2 - c[1]) * gridSize, // 翻转Y轴
            c[2] * zThickness // 使用 zThickness 确保最小厚度 5mm
          ])
          const i0 = getVertexIndex(corners[0][0], corners[0][1], corners[0][2])
          const i1 = getVertexIndex(corners[1][0], corners[1][1], corners[1][2])
          const i2 = getVertexIndex(corners[2][0], corners[2][1], corners[2][2])
          const i3 = getVertexIndex(corners[3][0], corners[3][1], corners[3][2])
          // 两个三角形，保证统一绕序
          triangles.push({ v1: i0, v2: i1, v3: i2, color: hexColor })
          triangles.push({ v1: i2, v2: i1, v3: i3, color: hexColor })
        }
      }
    }
    return { vertices, triangles }
  },

  /**
   * 计算模型缩放比例
   */
  calculateScale: function(vertices, scale) {
    if (vertices.length === 0) {
      return { scaleX: 1, scaleY: 1, scaleZ: 1 }
    }
    const minThickness = MODEL_CONSTRAINTS.MIN_THICKNESS
    // 计算边界框
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    let minZ = Infinity
    let maxZ = -Infinity

    for (const v of vertices) {
      minX = Math.min(minX, v.x)
      maxX = Math.max(maxX, v.x)
      minY = Math.min(minY, v.y)
      maxY = Math.max(maxY, v.y)
      minZ = Math.min(minZ, v.z)
      maxZ = Math.max(maxZ, v.z)
    }

    const sizeX = maxX - minX
    const sizeY = maxY - minY
    const sizeZ = maxZ - minZ
    const sizeMax = Math.max(sizeX, sizeY, sizeZ)
    const sizeMin = Math.min(sizeX, sizeY, sizeZ)

    let scaleX = 1
    let scaleY = 1
    let scaleZ = 1

    if (scale > 0) {
      // 使用指定的缩放比例
      scaleX = scale
      scaleY = scale
      scaleZ = scale
      // 确保缩放后的 Z 轴厚度至少为 5mm
      const scaledSizeZ = sizeZ * scaleZ
      if (scaledSizeZ < minThickness && sizeZ > 0) {
        scaleZ = minThickness / sizeZ
      } else if (sizeZ <= 0) {
        scaleZ = minThickness
      }
    } else {
      // 自动计算缩放
      let scaleRate = 1
      const maxScaleRate = sizeMax > MODEL_CONSTRAINTS.MAX_DIMENSION
        ? MODEL_CONSTRAINTS.MAX_DIMENSION / sizeMax
        : 1
      const minScaleRate = sizeMin < MODEL_CONSTRAINTS.MIN_DIMENSION
        ? MODEL_CONSTRAINTS.MIN_DIMENSION / sizeMin
        : 1

      if (sizeMax * minScaleRate <= MODEL_CONSTRAINTS.MAX_DIMENSION) {
        scaleRate = minScaleRate
      } else {
        scaleRate = maxScaleRate
      }

      scaleX = scaleRate
      scaleY = scaleRate
      scaleZ = scaleRate
      // 确保缩放后的 Z 轴厚度至少为 5mm
      const scaledSizeZ = sizeZ * scaleZ
      if (scaledSizeZ < minThickness && sizeZ > 0) {
        scaleZ = minThickness / sizeZ
      } else if (sizeZ <= 0) {
        scaleZ = minThickness
      }
    }

    return { scaleX, scaleY, scaleZ }
  },

  /**
   * 为三角形分配颜色（匹配 3MF 调色板）
   * 功能：
   * 1. 规范化颜色格式（确保有 # 前缀，转换为大写）
   * 2. 使用最佳匹配算法将原始颜色匹配到 d3PaletteArr
   * 3. 获取对应的颜色代码（d3PaletteIdx）
   * 4. 为每个三角形添加 paint_color 属性
   * @param {Array<{v1: number, v2: number, v3: number, color: string}>} triangles - 三角形数组（包含原始颜色）
   * @param {Object} sceneData - 场景数据（可选，用于调试）
   * @returns {Array<{v1: number, v2: number, v3: number, paint_color: string}>} 带颜色代码的三角形数组
   */
  assignColorsToTriangles: function(triangles, sceneData) {
    // 检查调色板是否有效
    if (!d3PaletteArr || d3PaletteArr.length === 0) {
      return triangles.map(triangle => ({
        v1: triangle.v1,
        v2: triangle.v2,
        v3: triangle.v3,
        paint_color: '4' // 默认白色
      }))
    }

    const sampleMatches = []
    const result = triangles.map((triangle, index) => {
      let hexColor = triangle.color
      // 如果没有颜色，使用默认白色
      if (!hexColor || typeof hexColor !== 'string' || hexColor.trim() === '') {
        hexColor = '#FFFFFF'
      } else {
        hexColor = hexColor.trim()
        // 确保有#前缀
        if (!hexColor.startsWith('#')) {
          hexColor = '#' + hexColor
        }
        // 转换为大写
        hexColor = hexColor.toUpperCase()
      }
      // 匹配调色板
      const matchedColor = utils.bestMatch(d3PaletteArr, hexColor)
      // 获取颜色代号
      let colorCode = '4' // 默认白色
      if (matchedColor && matchedColor !== '') {
        const colorIndex = d3PaletteArr.indexOf(matchedColor)
        if (colorIndex >= 0 && colorIndex < d3PaletteIdx.length) {
          colorCode = d3PaletteIdx[colorIndex]
          // 记录前几个匹配结果用于调试
          if (sampleMatches.length < 5 && colorCode !== '4') {
            sampleMatches.push({
              original: triangle.color,
              normalized: hexColor,
              matched: matchedColor,
              code: colorCode
            })
          }
        }
      }
      return {
        v1: triangle.v1,
        v2: triangle.v2,
        v3: triangle.v3,
        paint_color: colorCode
      }
    })

    return result
  },

  /**
   * 从三角形数组中提取使用的颜色列表（按首次出现的顺序）
   */
  getUsedColorsFromTriangles: function(triangles) {
    const colorMap = new Map()
    const colorOrder = []
    for (const triangle of triangles) {
      if (triangle.paint_color) {
        const paintColor = triangle.paint_color
        if (!colorMap.has(paintColor)) {
          const colorIndex = d3PaletteIdx.indexOf(paintColor)
          if (colorIndex >= 0 && colorIndex < d3PaletteArr.length) {
            const hexColor = d3PaletteArr[colorIndex]
            colorMap.set(paintColor, hexColor)
            colorOrder.push(hexColor)
          }
        }
      }
    }

    // 如果没有颜色，至少添加白色
    if (colorOrder.length === 0) {
      colorOrder.push('#FFFFFF')
    }
    return colorOrder
  },

  /**
   * 重新映射 paint_color 值
   */
  remapPaintColors: function(triangles, usedColors) {
    // 创建 hexColor -> usedColors 索引 -> paint_color 的映射
    // 关键：paint_color 应该对应 usedColors 数组的索引
    const hexColorToPaintColor = new Map()
    for (let i = 0; i < usedColors.length; i++) {
      const hexColor = usedColors[i]
      // 使用 usedColors 的索引对应的 paint_color
      if (i < d3PaletteIdx.length) {
        const paintColor = d3PaletteIdx[i]
        hexColorToPaintColor.set(hexColor.toUpperCase(), paintColor)
      }
    }
    // 创建原始 paint_color -> hexColor 的映射（用于查找原始颜色）
    const paintColorToHexColor = new Map()
    for (let i = 0; i < d3PaletteIdx.length && i < d3PaletteArr.length; i++) {
      paintColorToHexColor.set(d3PaletteIdx[i], d3PaletteArr[i].toUpperCase())
    }
    // 重新映射所有三角形的 paint_color
    const remapped = triangles.map(triangle => {
      const oldPaintColor = triangle.paint_color || '4'
      // 找到原始 paint_color 对应的 hexColor
      const hexColor = paintColorToHexColor.get(oldPaintColor)
      let newPaintColor = '4' // 默认白色
      if (hexColor) {
        // 查找该 hexColor 在 usedColors 中的位置
        const hexColorUpper = hexColor.toUpperCase()
        if (hexColorToPaintColor.has(hexColorUpper)) {
          newPaintColor = hexColorToPaintColor.get(hexColorUpper)
        } else {
          // 如果找不到，尝试在 usedColors 中查找（处理大小写问题）
          const foundIndex = usedColors.findIndex(c => c.toUpperCase() === hexColorUpper)
          if (foundIndex >= 0 && foundIndex < d3PaletteIdx.length) {
            newPaintColor = d3PaletteIdx[foundIndex]
          } else if (usedColors.length > 0) {
            // 如果还是找不到，使用第一个颜色
            newPaintColor = hexColorToPaintColor.get(usedColors[0].toUpperCase()) || '4'
          }
        }
      } else if (usedColors.length > 0) {
        // 如果找不到原始颜色，使用第一个颜色
        newPaintColor = hexColorToPaintColor.get(usedColors[0].toUpperCase()) || '4'
      }
      return {
        v1: triangle.v1,
        v2: triangle.v2,
        v3: triangle.v3,
        paint_color: newPaintColor
      }
    })
    return remapped
  },

  /**
   * 生成 3MF 格式的 XML 文件内容
   */
  generate3mfXml: function(vertices, triangles) {
    const header = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" xmlns:BambuStudio="http://schemas.bambulab.com/package/2021" xmlns:p="http://schemas.microsoft.com/3dmanufacturing/production/2015/06" requiredextensions="p">\n` +
      ` <metadata name="BambuStudio:3mfVersion">1</metadata>\n` +
      ` <resources>\n` +
      `  <object id="1" p:UUID="000e0000-81cb-4c03-9d28-80fed5dfa1dc" type="model">\n` +
      `   <mesh>\n` +
      `    <vertices>\n`

    let verticesXml = ''
    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i]
      verticesXml += `     <vertex x="${v.x}" y="${v.y}" z="${v.z}"/>\n`
    }

    let trianglesXml = ''
    for (let i = 0; i < triangles.length; i++) {
      const t = triangles[i]
      const paintColor = t.paint_color || '4'
      const colorAttr = ` paint_color="${paintColor}"`
      trianglesXml += `     <triangle v1="${t.v1}" v2="${t.v2}" v3="${t.v3}"${colorAttr}/>\n`
    }

    const tail = verticesXml + `    </vertices>\n` +
      `    <triangles>\n` +
      trianglesXml +
      `    </triangles>\n` +
      `   </mesh>\n` +
      `  </object>\n` +
      ` </resources>\n` +
      ` <build>\n` +
      `  <item objectid="1"/>\n` +
      ` </build>\n` +
      `</model>\n`

    return header + tail
  },

  /**
   * 创建 3MF 项目配置文件
   * @param {Object} jsonConf - 从模板中读取的现有配置（如果存在）
   * @param {Array<string>} paletteArr - 使用的颜色数组（十六进制格式）
   * @returns {Object} 更新后的项目配置
   */
  createProjectConfig: function(jsonConf, paletteArr) {
    // 如果 jsonConf 为空或不是对象，初始化为空对象
    if (!jsonConf || typeof jsonConf !== 'object') {
      jsonConf = {}
    }
    const colorNums = paletteArr.length
    const defaultValue = {
      'activate_air_filtration': '0',
      'additional_cooling_fan_speed': '70',
      'chamber_temperatures': '0',
      'circle_compensation_speed': '200',
      'close_fan_the_first_x_layers': '1',
      'complete_print_exhaust_fan_speed': '70',
      'cool_plate_temp': '35',
      'cool_plate_temp_initial_layer': '35',
      'counter_coef_1': '0',
      'counter_coef_2': '0.008',
      'counter_coef_3': '-0.041',
      'counter_limit_max': '0.033',
      'counter_limit_min': '-0.035',
      'default_filament_colour': '',
      'diameter_limit': '50',
      'different_settings_to_system': '',
      'during_print_exhaust_fan_speed': '70',
      'enable_overhang_bridge_fan': '1',
      'enable_pressure_advance': '0',
      'eng_plate_temp': '0',
      'eng_plate_temp_initial_layer': '0',
      'fan_cooling_layer_time': '100',
      'fan_max_speed': '100',
      'fan_min_speed': '100',
      'filament_adaptive_volumetric_speed': ['0', '0'],
      'filament_adhesiveness_category': '100',
      'filament_change_length': '5',
      'filament_colour_type': '1',
      'filament_cost': '24.99',
      'filament_density': '1.26',
      'filament_deretraction_speed': ['nil', 'nil'],
      'filament_diameter': '1.75',
      'filament_end_gcode': '; filament end gcode \\n\\n',
      'filament_extruder_variant': ['Direct Drive Standard', 'Direct Drive High Flow'],
      'filament_flow_ratio': ['0.98', '0.98'],
      'filament_flush_temp': ['0', '0'],
      'filament_flush_volumetric_speed': ['0', '0'],
      'filament_ids': 'GFA00',
      'filament_is_support': '0',
      'filament_long_retractions_when_cut': ['1', '1'],
      'filament_map': '1',
      'filament_max_volumetric_speed': ['21', '21'],
      // 'filament_max_volumetric_speed': ['2', '2'],
      'filament_minimal_purge_on_wipe_tower': '15',
      'filament_pre_cooling_temperature': ['0', '0'],
      'filament_prime_volume': '30',
      'filament_printable': '3',
      'filament_ramming_travel_time': ['0', '0'],
      'filament_ramming_volumetric_speed': ['-1', '-1'],
      'filament_retract_before_wipe': ['nil', 'nil'],
      'filament_retract_restart_extra': ['nil', 'nil'],
      'filament_retract_when_changing_layer': ['nil', 'nil'],
      'filament_retraction_distances_when_cut': ['18', '18'],
      'filament_retraction_length': ['nil', 'nil'],
      'filament_retraction_minimum_travel': ['nil', 'nil'],
      'filament_retraction_speed': ['nil', 'nil'],
      'filament_scarf_gap': '0%',
      'filament_scarf_height': '10%',
      'filament_scarf_length': '10',
      'filament_scarf_seam_type': 'none',
      'filament_settings_id': 'Bambu PLA Basic @BBL X1C',
      // 'filament_settings_id': 'Bambu PLA Basic @BBL X1C 0.2 nozzle',
      'filament_shrink': '100%',
      'filament_soluble': '0',
      'filament_start_gcode': '; filament start gcode\\n{if  (bed_temperature[current_extruder] >55)||(bed_temperature_initial_layer[current_extruder] >55)}M106 P3 S200\\n{elsif(bed_temperature[current_extruder] >50)||(bed_temperature_initial_layer[current_extruder] >50)}M106 P3 S150\\n{elsif(bed_temperature[current_extruder] >45)||(bed_temperature_initial_layer[current_extruder] >45)}M106 P3 S50\\n{endif}\\nM142 P1 R35 S40\\n{if activate_air_filtration[current_extruder] && support_air_filtration}\\nM106 P3 S{during_print_exhaust_fan_speed_num[current_extruder]} \\n{endif}',
      // 'filament_start_gcode': '; filament start gcode\\n{if  (bed_temperature[current_extruder] >55)||(bed_temperature_initial_layer[current_extruder] >55)}M106 P3 S200\\n{elsif(bed_temperature[current_extruder] >50)||(bed_temperature_initial_layer[current_extruder] >50)}M106 P3 S150\\n{elsif(bed_temperature[current_extruder] >45)||(bed_temperature_initial_layer[current_extruder] >45)}M106 P3 S50\\n{endif}\\nM142 P1 R35 S40\\n{if activate_air_filtration[current_extruder] && support_air_filtration}\\nM106 P3 S{during_print_exhaust_fan_speed_num[current_extruder]} \\n{endif}',
      'filament_type': 'PLA',
      'filament_velocity_adaptation_factor': '1',
      'filament_vendor': 'Bambu Lab',
      'filament_wipe': ['nil', 'nil'],
      'filament_wipe_distance': ['nil', 'nil'],
      'filament_z_hop': ['nil', 'nil'],
      'filament_z_hop_types': ['nil', 'nil'],
      'first_x_layer_fan_speed': '0',
      // 该参数暂时无法计算
      'flush_volumes_matrix': ['0', '0'],
      'flush_volumes_vector': ['140', '140'],
      'full_fan_speed_layer': '0',
      'hole_coef_1': '0',
      'hole_coef_2': '-0.008',
      'hole_coef_3': '0.23415',
      'hole_limit_max': '0.22',
      'hole_limit_min': '0.088',
      'hot_plate_temp': '55',
      'hot_plate_temp_initial_layer': '55',
      'impact_strength_z': '13.8',
      'long_retractions_when_ec': ['0', '0'],
      'no_slow_down_for_cooling_on_outwalls': '0',
      'nozzle_temperature': ['220', '220'],
      'nozzle_temperature_initial_layer': ['220', '220'],
      'nozzle_temperature_range_high': '240',
      'nozzle_temperature_range_low': '190',
      'overhang_fan_speed': '100',
      'overhang_fan_threshold': '50%',
      'overhang_threshold_participating_cooling': '95%',
      'pre_start_fan_time': '0',
      'pressure_advance': '0.02',
      'reduce_fan_stop_start_freq': '1',
      'required_nozzle_HRC': '3',
      'retraction_distances_when_ec': ['0', '0'],
      'slow_down_for_layer_cooling': '1',
      'slow_down_layer_time': '4',
      'slow_down_min_speed': '20',
      'supertack_plate_temp': '45',
      'supertack_plate_temp_initial_layer': '45',
      'temperature_vitrification': '45',
      'textured_plate_temp': '55',
      'textured_plate_temp_initial_layer': '55',
      'volumetric_speed_coefficients': ['0 0 0 0 0 0', '0 0 0 0 0 0']
    }

    // 根据 defaultValue 生成/覆盖对应 key 的数组值
    const buildArray = function(val, len) {
      if (Array.isArray(val)) {
        const out = new Array(len)
        const base = val.length > 0 ? val : ['']
        for (let i = 0; i < len; i++) out[i] = base[i % base.length]
        return out
      }
      const out = new Array(len)
      for (let i = 0; i < len; i++) out[i] = val
      return out
    }

    // 更新配置：如果 jsonConf 中已有该 key，保留原有值；否则使用默认值
    Object.keys(defaultValue).forEach((key) => {
      const defVal = defaultValue[key]
      const isArray = Array.isArray(defVal)
      const targetLen = isArray ? colorNums * defVal.length : colorNums

      // 如果 jsonConf 中已有该 key，检查是否需要扩展数组长度
      if (Object.prototype.hasOwnProperty.call(jsonConf, key)) {
        const existingVal = jsonConf[key]
        if (Array.isArray(existingVal)) {
          // 如果现有值是数组，检查长度是否匹配
          if (existingVal.length !== targetLen) {
            // 长度不匹配，使用默认值重新构建
            jsonConf[key] = buildArray(defVal, targetLen)
          }
          // 如果长度匹配，保留原有值
        } else {
          // 如果现有值不是数组，但默认值需要数组，则使用默认值构建
          if (isArray || targetLen > 1) {
            jsonConf[key] = buildArray(defVal, targetLen)
          }
          // 如果都是标量，保留原有值
        }
      } else {
        // 如果 jsonConf 中没有该 key，使用默认值构建
        jsonConf[key] = buildArray(defVal, targetLen)
      }
    })

    jsonConf.filament_colour = paletteArr
    jsonConf.filament_multi_colour = paletteArr
    jsonConf.different_settings_to_system = jsonConf.different_settings_to_system || []
    jsonConf.different_settings_to_system.unshift('enable_support')

    const filament_self_index = []
    for (let i = 1; i <= colorNums; i++) {
      filament_self_index.push(i.toString())
      filament_self_index.push(i.toString())
    }
    jsonConf.filament_self_index = filament_self_index

    return jsonConf
  }
}
export default main
